import React, { useEffect, useRef, useState } from 'react';
import { certificationsData } from '../data/mock';
import { ShieldCheck, Lock, Network, Award, BadgeCheck, Hash } from 'lucide-react';
import { Badge } from './ui/badge';

const iconMap = {
  shield: ShieldCheck,
  lock: Lock,
  network: Network,
  award: Award,
};

function useInView(ref) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return visible;
}

export default function CertificationsSection() {
  const sectionRef = useRef(null);
  const visible = useInView(sectionRef);

  const totalCerts = certificationsData.reduce((sum, cat) => sum + cat.certs.length, 0);

  return (
    <section id="certifications" className="relative" ref={sectionRef}>
      <div className="section-container">
        <div className={`fade-in-up ${visible ? 'visible' : ''}`}>
          <p className="section-label">// Credentials & Certifications</p>
          <div className="flex flex-wrap items-end gap-4 mb-2">
            <h2 className="section-title mb-0">Professional Certifications</h2>
            <span className="font-['JetBrains_Mono'] text-sm text-[#00ff88] mb-1">{totalCerts}+ credentials</span>
          </div>
          <div className="glow-line w-20 mb-12" />
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {certificationsData.map((cat, catIdx) => (
            <div
              key={cat.category}
              className={`cyber-card p-6 fade-in-up ${visible ? 'visible' : ''}`}
              style={{ transitionDelay: `${0.1 + catIdx * 0.12}s` }}
            >
              {/* Category header */}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-lg bg-[#00e5ff]/8 border border-[#00e5ff]/20 flex items-center justify-center">
                  <BadgeCheck className="w-4.5 h-4.5 text-[#00e5ff]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#f1f5f9] text-[15px]">{cat.category}</h3>
                  <p className="text-[10px] text-[#64748b] font-['JetBrains_Mono']">{cat.certs.length} certifications</p>
                </div>
              </div>

              {/* Certs list */}
              <div className="space-y-3">
                {cat.certs.map((cert, idx) => {
                  const Icon = iconMap[cert.icon] || ShieldCheck;
                  return (
                    <div
                      key={idx}
                      className="group flex gap-3 p-3 rounded-lg border border-transparent hover:border-[#1e293b] hover:bg-[#0c1017]/50 transition-colors duration-200"
                    >
                      <div className="w-8 h-8 rounded-md bg-[#111827] border border-[#1e293b] flex items-center justify-center flex-shrink-0 group-hover:border-[#00e5ff]/30 transition-colors duration-200">
                        <Icon className="w-3.5 h-3.5 text-[#64748b] group-hover:text-[#00e5ff] transition-colors duration-200" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-[#c8d1dc] font-medium leading-snug group-hover:text-[#f1f5f9] transition-colors duration-200">
                          {cert.name}
                        </p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                          <span className="text-[11px] text-[#00e5ff] font-['JetBrains_Mono']">{cert.issuer}</span>
                          {cert.date && (
                            <span className="text-[10px] text-[#64748b] font-['JetBrains_Mono']">{cert.date}</span>
                          )}
                          {cert.credId && (
                            <span className="flex items-center gap-1 text-[10px] text-[#4a5568] font-['JetBrains_Mono']">
                              <Hash className="w-2.5 h-2.5" />{cert.credId}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Summary bar */}
        <div className={`mt-10 flex flex-wrap justify-center gap-4 fade-in-up ${visible ? 'visible' : ''}`} style={{ transitionDelay: '0.6s' }}>
          {['EC-Council', 'ISC2', 'Cisco', 'Palo Alto', 'Fortinet', 'SANS'].map((org) => (
            <Badge
              key={org}
              variant="outline"
              className="border-[#1e293b] text-[#64748b] bg-transparent text-[11px] font-['JetBrains_Mono'] py-1.5 px-3 hover:border-[#00e5ff]/30 hover:text-[#00e5ff] transition-colors duration-200"
            >
              {org}
            </Badge>
          ))}
        </div>
      </div>
    </section>
  );
}
